import { useEffect, useState } from "react";
import {
  listBenchmarks,
  createBenchmark,
  updateBenchmark,
  deleteBenchmark,
  addBenchmarkAllocation,
  deleteBenchmarkAllocation,
  addBenchmarkCashRate,
  deleteBenchmarkCashRate,
  getBenchmarkEvolution,
  type Benchmark,
} from "../api/client";

interface Props {
  portfolioId: number | null;
}

const today = new Date().toISOString().slice(0, 10);

interface NewBmForm { name: string; cash_rate: string }
interface AllocForm { symbol: string; quantity: string; start_date: string }
interface CashForm { currency: string; amount: string; annual_rate: string; start_date: string }
interface BmEdit { name: string; cash_rate: string }

const inputStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: 5,
  padding: "4px 8px",
  fontSize: 13,
  outline: "none",
};

const btnPrimary: React.CSSProperties = {
  background: "#3b82f6",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "5px 14px",
  cursor: "pointer",
  fontSize: 13,
};

const btnSecondary: React.CSSProperties = {
  background: "none",
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  padding: "5px 14px",
  cursor: "pointer",
  fontSize: 13,
};

const btnDanger: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "#dc2626",
  fontSize: 13,
};

export default function BenchmarkPage({ portfolioId }: Props) {
  const [benchmarks, setBenchmarks] = useState<Benchmark[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [valuations, setValuations] = useState<Record<number, { value: number; date: string }>>({});

  // New benchmark form
  const [newBmOpen, setNewBmOpen] = useState(false);
  const [newBm, setNewBm] = useState<NewBmForm>({ name: "", cash_rate: "0" });
  const [newBmSaving, setNewBmSaving] = useState(false);

  // Per-benchmark edit state (name + cash_rate)
  const [bmEdits, setBmEdits] = useState<Record<number, BmEdit>>({});
  const [bmSaving, setBmSaving] = useState<Record<number, boolean>>({});

  // Per-benchmark allocation form
  const [allocForms, setAllocForms] = useState<Record<number, AllocForm>>({});

  // Per-benchmark cash form
  const [cashForms, setCashForms] = useState<Record<number, CashForm>>({});

  const load = () => {
    if (!portfolioId) return;
    setLoading(true);
    listBenchmarks(portfolioId)
      .then((res: { data: Benchmark[] }) => {
        setBenchmarks(res.data);
        // Fetch latest valuation for each benchmark
        res.data.forEach((bm: Benchmark) => {
          getBenchmarkEvolution(portfolioId, bm.id)
            .then((evoRes: { data: { benchmark_value: number; date: string }[] }) => {
              if (evoRes.data.length > 0) {
                const last = evoRes.data[evoRes.data.length - 1];
                setValuations((prev) => ({
                  ...prev,
                  [bm.id]: { value: last.benchmark_value, date: last.date },
                }));
              }
            })
            .catch(() => {/* ignore */});
        });
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId]);

  // ---- Create benchmark ----
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!portfolioId || !newBm.name.trim()) return;
    setNewBmSaving(true);
    try {
      const cash_rate = parseFloat(newBm.cash_rate) / 100;
      const res = await createBenchmark(portfolioId, { name: newBm.name.trim(), cash_rate: isNaN(cash_rate) ? 0 : cash_rate });
      setBenchmarks((prev) => [...prev, res.data]);
      setNewBm({ name: "", cash_rate: "0" });
      setNewBmOpen(false);
    } catch {
      alert("Failed to create benchmark");
    } finally {
      setNewBmSaving(false);
    }
  };

  // ---- Update benchmark (name + cash_rate) ----
  const handleUpdate = async (bm: Benchmark) => {
    const edit = bmEdits[bm.id];
    if (!edit) return;
    setBmSaving((p) => ({ ...p, [bm.id]: true }));
    try {
      const cash_rate = parseFloat(edit.cash_rate) / 100;
      const res = await updateBenchmark(bm.id, {
        name: edit.name.trim() || bm.name,
        cash_rate: isNaN(cash_rate) ? Number(bm.cash_rate) : cash_rate,
      });
      setBenchmarks((prev) => prev.map((b) => (b.id === bm.id ? res.data : b)));
      setBmEdits((p) => { const n = { ...p }; delete n[bm.id]; return n; });
    } catch {
      alert("Failed to update benchmark");
    } finally {
      setBmSaving((p) => ({ ...p, [bm.id]: false }));
    }
  };

  const handleDelete = async (bm: Benchmark) => {
    if (!confirm(`Delete benchmark "${bm.name}"?`)) return;
    try {
      await deleteBenchmark(bm.id);
      setBenchmarks((prev) => prev.filter((b) => b.id !== bm.id));
    } catch {
      alert("Failed to delete benchmark");
    }
  };

  // ---- Allocation form ----
  const openAllocForm = (bmId: number) =>
    setAllocForms((p) => ({ ...p, [bmId]: { symbol: "", quantity: "", start_date: today } }));

  const handleAddAllocation = async (e: React.FormEvent, bm: Benchmark) => {
    e.preventDefault();
    const form = allocForms[bm.id];
    if (!form) return;
    const symbol = form.symbol.trim().toUpperCase();
    const quantity = parseFloat(form.quantity);
    if (!symbol || isNaN(quantity) || quantity <= 0) { alert("Invalid symbol or quantity"); return; }
    try {
      const res = await addBenchmarkAllocation(bm.id, { symbol, quantity, start_date: form.start_date });
      setBenchmarks((prev) =>
        prev.map((b) =>
          b.id === bm.id ? { ...b, allocations: [...b.allocations, res.data] } : b
        )
      );
      setAllocForms((p) => { const n = { ...p }; delete n[bm.id]; return n; });
    } catch {
      alert("Failed to add allocation");
    }
  };

  const handleDeleteAllocation = async (bm: Benchmark, allocationId: number) => {
    if (!confirm("Remove this allocation?")) return;
    try {
      await deleteBenchmarkAllocation(bm.id, allocationId);
      setBenchmarks((prev) =>
        prev.map((b) =>
          b.id === bm.id
            ? { ...b, allocations: b.allocations.filter((a) => a.id !== allocationId) }
            : b
        )
      );
    } catch {
      alert("Failed to delete allocation");
    }
  };

  // ---- Cash rate form ----
  const openCashForm = (bmId: number) =>
    setCashForms((p) => ({ ...p, [bmId]: { currency: "", amount: "", annual_rate: "", start_date: today } }));

  const handleAddCashRate = async (e: React.FormEvent, bm: Benchmark) => {
    e.preventDefault();
    const form = cashForms[bm.id];
    if (!form) return;
    const currency = form.currency.trim().toUpperCase();
    const amount = parseFloat(form.amount);
    if (!currency || isNaN(amount)) { alert("Invalid currency or amount"); return; }
    const annual_rate = form.annual_rate.trim() === "" ? null : parseFloat(form.annual_rate) / 100;
    try {
      const res = await addBenchmarkCashRate(bm.id, { currency, amount, annual_rate, start_date: form.start_date });
      setBenchmarks((prev) =>
        prev.map((b) =>
          b.id === bm.id ? { ...b, cash_rates: [...(b.cash_rates ?? []), res.data] } : b
        )
      );
      setCashForms((p) => { const n = { ...p }; delete n[bm.id]; return n; });
    } catch {
      alert("Failed to add cash entry");
    }
  };

  const handleDeleteCashRate = async (bm: Benchmark, cashRateId: number) => {
    if (!confirm("Remove this cash entry?")) return;
    try {
      await deleteBenchmarkCashRate(bm.id, cashRateId);
      setBenchmarks((prev) =>
        prev.map((b) =>
          b.id === bm.id
            ? { ...b, cash_rates: (b.cash_rates ?? []).filter((c) => c.id !== cashRateId) }
            : b
        )
      );
    } catch {
      alert("Failed to delete cash entry");
    }
  };

  const fmtUSD = (v: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);

  if (!portfolioId) return <p style={{ color: "#64748b" }}>Select a portfolio.</p>;
  if (loading) return <p>Loading…</p>;
  if (error) return <p style={{ color: "red" }}>Error: {error}</p>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
        <h2 style={{ margin: 0, color: "#1e293b" }}>Benchmarks</h2>
        {!newBmOpen && (
          <button onClick={() => setNewBmOpen(true)} style={btnPrimary}>
            + New Benchmark
          </button>
        )}
      </div>

      {/* New benchmark form */}
      {newBmOpen && (
        <form
          onSubmit={handleCreate}
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "flex-end",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>Name</label>
            <input
              required
              autoFocus
              placeholder="e.g. 60/40 Portfolio"
              value={newBm.name}
              onChange={(e) => setNewBm((p) => ({ ...p, name: e.target.value }))}
              style={{ ...inputStyle, width: 220 }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>Default Cash Rate (%/yr)</label>
            <input
              type="number"
              step="0.001"
              min="0"
              placeholder="e.g. 4.5"
              value={newBm.cash_rate}
              onChange={(e) => setNewBm((p) => ({ ...p, cash_rate: e.target.value }))}
              style={{ ...inputStyle, width: 140 }}
            />
          </div>
          <button type="submit" disabled={newBmSaving} style={btnPrimary}>
            {newBmSaving ? "Creating…" : "Create"}
          </button>
          <button type="button" onClick={() => setNewBmOpen(false)} style={btnSecondary}>
            Cancel
          </button>
        </form>
      )}

      {benchmarks.length === 0 && !newBmOpen && (
        <div style={{ color: "#94a3b8", textAlign: "center", padding: "40px 0" }}>
          <p>No benchmarks yet.</p>
          <p style={{ fontSize: 13 }}>
            A benchmark is a buy-and-hold portfolio of stocks with optional cash positions accruing at per-currency rates.
          </p>
        </div>
      )}

      {benchmarks.map((bm) => {
        const edit = bmEdits[bm.id];
        const saving = bmSaving[bm.id] ?? false;
        const allocForm = allocForms[bm.id];
        const cashForm = cashForms[bm.id];
        const defaultRatePct = (Number(bm.cash_rate) * 100).toFixed(3);

        return (
          <div
            key={bm.id}
            style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20, marginBottom: 20 }}
          >
            {/* Benchmark header — editable name + cash rate */}
            <div style={{ display: "flex", alignItems: "flex-end", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              {edit ? (
                <>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Name</label>
                    <input
                      autoFocus
                      value={edit.name}
                      onChange={(e) => setBmEdits((p) => ({ ...p, [bm.id]: { ...edit, name: e.target.value } }))}
                      style={{ ...inputStyle, fontSize: 15, fontWeight: 700, width: 220 }}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Default Cash Rate (%/yr)</label>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      value={edit.cash_rate}
                      onChange={(e) => setBmEdits((p) => ({ ...p, [bm.id]: { ...edit, cash_rate: e.target.value } }))}
                      style={{ ...inputStyle, width: 120 }}
                    />
                  </div>
                  <button onClick={() => handleUpdate(bm)} disabled={saving} style={btnPrimary}>
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    onClick={() => setBmEdits((p) => { const n = { ...p }; delete n[bm.id]; return n; })}
                    style={btnSecondary}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <div>
                    <h3 style={{ margin: 0, color: "#1e293b" }}>{bm.name}</h3>
                    <span style={{ fontSize: 12, color: "#64748b" }}>
                      Default cash rate: <strong>{defaultRatePct}%/yr</strong>
                    </span>
                    {valuations[bm.id] && (
                      <div style={{ marginTop: 4 }}>
                        <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 700 }}>
                          {fmtUSD(valuations[bm.id].value)}
                        </span>
                        <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: 6 }}>
                          as of {valuations[bm.id].date}
                        </span>
                      </div>
                    )}
                  </div>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    <button
                      onClick={() => setBmEdits((p) => ({ ...p, [bm.id]: { name: bm.name, cash_rate: defaultRatePct } }))}
                      style={btnSecondary}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(bm)}
                      style={{ ...btnSecondary, border: "1px solid #fca5a5", color: "#dc2626" }}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Equity Allocations */}
            <h4 style={{ margin: "0 0 8px", color: "#475569", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Equity Allocations
            </h4>
            <div style={{ overflowX: "auto", marginBottom: 10 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {["Symbol", "Quantity (shares)", "Start Date", ""].map((h) => (
                      <th key={h} style={{ padding: "6px 12px", textAlign: "left", color: "#64748b", fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bm.allocations.length === 0 && !allocForm && (
                    <tr>
                      <td colSpan={4} style={{ padding: "12px", color: "#94a3b8", textAlign: "center" }}>
                        No equity positions.
                      </td>
                    </tr>
                  )}
                  {bm.allocations.map((alloc) => (
                    <tr key={alloc.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "6px 12px", fontWeight: 700, fontFamily: "monospace" }}>{alloc.symbol}</td>
                      <td style={{ padding: "6px 12px", textAlign: "right" }}>{Number(alloc.quantity).toLocaleString()}</td>
                      <td style={{ padding: "6px 12px" }}>{alloc.start_date}</td>
                      <td style={{ padding: "6px 12px" }}>
                        <button onClick={() => handleDeleteAllocation(bm, alloc.id)} style={btnDanger}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {allocForm && (
                    <tr style={{ background: "#f0f9ff" }}>
                      <td style={{ padding: "6px 8px" }}>
                        <input
                          required
                          autoFocus
                          placeholder="SPY"
                          value={allocForm.symbol}
                          onChange={(e) => setAllocForms((p) => ({ ...p, [bm.id]: { ...allocForm, symbol: e.target.value } }))}
                          style={{ ...inputStyle, width: 80, textTransform: "uppercase" }}
                        />
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        <input
                          required
                          type="number"
                          min="0"
                          step="any"
                          placeholder="100"
                          value={allocForm.quantity}
                          onChange={(e) => setAllocForms((p) => ({ ...p, [bm.id]: { ...allocForm, quantity: e.target.value } }))}
                          style={{ ...inputStyle, width: 100 }}
                        />
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        <input
                          required
                          type="date"
                          value={allocForm.start_date}
                          onChange={(e) => setAllocForms((p) => ({ ...p, [bm.id]: { ...allocForm, start_date: e.target.value } }))}
                          style={inputStyle}
                        />
                      </td>
                      <td style={{ padding: "6px 8px", display: "flex", gap: 6 }}>
                        <button
                          onClick={(e) => handleAddAllocation(e as unknown as React.FormEvent, bm)}
                          style={btnPrimary}
                        >
                          Add
                        </button>
                        <button
                          onClick={() => setAllocForms((p) => { const n = { ...p }; delete n[bm.id]; return n; })}
                          style={btnSecondary}
                        >
                          Cancel
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {!allocForm && (
              <button
                onClick={() => openAllocForm(bm.id)}
                style={{
                  marginBottom: 20,
                  background: "transparent",
                  border: "1px dashed #94a3b8",
                  borderRadius: 6,
                  padding: "5px 14px",
                  cursor: "pointer",
                  color: "#64748b",
                  fontSize: 13,
                }}
              >
                + Add Equity Position
              </button>
            )}
            {allocForm && <div style={{ marginBottom: 20 }} />}

            {/* Cash Positions */}
            <h4 style={{ margin: "0 0 8px", color: "#475569", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Cash Positions
            </h4>
            <p style={{ margin: "0 0 8px", fontSize: 12, color: "#94a3b8" }}>
              Each entry sets a starting cash amount on a date with an accrual rate. Add a new entry for the same currency to change the rate or deposit more cash.
            </p>
            <div style={{ overflowX: "auto", marginBottom: 10 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {["Currency", "Amount", "Annual Rate", "Start Date", ""].map((h) => (
                      <th key={h} style={{ padding: "6px 12px", textAlign: "left", color: "#64748b", fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(bm.cash_rates ?? []).length === 0 && !cashForm && (
                    <tr>
                      <td colSpan={5} style={{ padding: "12px", color: "#94a3b8", textAlign: "center" }}>
                        No cash positions.
                      </td>
                    </tr>
                  )}
                  {[...(bm.cash_rates ?? [])]
                    .sort((a, b) => a.currency.localeCompare(b.currency) || a.start_date.localeCompare(b.start_date))
                    .map((cr) => (
                      <tr key={cr.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "6px 12px", fontWeight: 700, fontFamily: "monospace" }}>{cr.currency}</td>
                        <td style={{ padding: "6px 12px", textAlign: "right" }}>{fmtUSD(cr.amount)}</td>
                        <td style={{ padding: "6px 12px", textAlign: "right" }}>
                          {cr.annual_rate !== null
                            ? `${(Number(cr.annual_rate) * 100).toFixed(3)}%`
                            : <span style={{ color: "#94a3b8", fontStyle: "italic" }}>default ({defaultRatePct}%)</span>
                          }
                        </td>
                        <td style={{ padding: "6px 12px" }}>{cr.start_date}</td>
                        <td style={{ padding: "6px 12px" }}>
                          <button onClick={() => handleDeleteCashRate(bm, cr.id)} style={btnDanger}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  {cashForm && (
                    <tr style={{ background: "#f0fdf4" }}>
                      <td style={{ padding: "6px 8px" }}>
                        <input
                          required
                          autoFocus
                          placeholder="USD"
                          value={cashForm.currency}
                          onChange={(e) => setCashForms((p) => ({ ...p, [bm.id]: { ...cashForm, currency: e.target.value } }))}
                          style={{ ...inputStyle, width: 70, textTransform: "uppercase" }}
                        />
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        <input
                          required
                          type="number"
                          step="any"
                          placeholder="10000"
                          value={cashForm.amount}
                          onChange={(e) => setCashForms((p) => ({ ...p, [bm.id]: { ...cashForm, amount: e.target.value } }))}
                          style={{ ...inputStyle, width: 110 }}
                        />
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          placeholder={`default (${defaultRatePct}%)`}
                          value={cashForm.annual_rate}
                          onChange={(e) => setCashForms((p) => ({ ...p, [bm.id]: { ...cashForm, annual_rate: e.target.value } }))}
                          style={{ ...inputStyle, width: 130 }}
                        />
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        <input
                          required
                          type="date"
                          value={cashForm.start_date}
                          onChange={(e) => setCashForms((p) => ({ ...p, [bm.id]: { ...cashForm, start_date: e.target.value } }))}
                          style={inputStyle}
                        />
                      </td>
                      <td style={{ padding: "6px 8px", display: "flex", gap: 6 }}>
                        <button
                          onClick={(e) => handleAddCashRate(e as unknown as React.FormEvent, bm)}
                          style={btnPrimary}
                        >
                          Add
                        </button>
                        <button
                          onClick={() => setCashForms((p) => { const n = { ...p }; delete n[bm.id]; return n; })}
                          style={btnSecondary}
                        >
                          Cancel
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {!cashForm && (
              <button
                onClick={() => openCashForm(bm.id)}
                style={{
                  background: "transparent",
                  border: "1px dashed #94a3b8",
                  borderRadius: 6,
                  padding: "5px 14px",
                  cursor: "pointer",
                  color: "#64748b",
                  fontSize: 13,
                }}
              >
                + Add Cash Entry
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
